import React, { useMemo, useCallback, useEffect } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Spinner } from 'react-bootstrap'
import { useWithdrawalsState } from '../../stores/WithdrawalsProvider.js'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest.jsx'
import TableProvider, { useTableState } from '../Table/TableProvider.jsx'
import useMountEffect from '../../utils/hooks/useMountEffect.js'
import { t } from 'i18next'
import { columns, shippingNoFilter} from './filterConfig.js'
import styled from 'styled-components'
import StructureIcon from '../Icons/StructureIcon.jsx'
import OverflowTooltip from '../OverflowTooltip.jsx'

const StyledStructureButton = styled.button`
  border: 0;
  padding: 0;
  background: none;
  @media (max-width: 926px) {
    svg {
      width: 100% !important;
    }
  }
`
const StyledHeaderStructure = styled.div`
  display: flex;
  align-items: flex-end;

  // @media (max-width: 869px) {
  //   flex-direction: column !important;
  // }
`

const defaultSorted = [
  {
    dataField: 'orderCreatedAt',
    order: 'desc',
  },
]

const WithdrawalTableInner = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    withdrawals,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    resetFilter,
    selectedRow,
    setShowAllWithdrawals,
    setSorting,
    tableIsLoading,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
    setOpenOfficeFilter,
    setOpenCostCenterFilter,
    setOpenStorehouseFilter,
    setOpenOrderProcessTypeFilter,
    setShowOverlayLeft,
    filterSOHeader,
    setFilterSOHeader,
  } = useWithdrawalsState()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()

  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })

  useEffect(() => {
    if (!!filterSOHeader) {
      setTimeout(() => {
        resetFilter()
        shippingNoFilter(filterSOHeader)
        setFilter((prev) => ({
          ...prev,
          'filter.shippingNo': filterSOHeader,
        }))
      }, 50)
      setFilterSOHeader(null)
    }
  }, [filterSOHeader, setFilterSOHeader, resetFilter, setFilter])

  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllWithdrawals(false)
      const { filters } = newState

      setFilter((prev) => ({
        ...prev,
        'filter.shippingNo': filters.shippingNo
          ? filters.shippingNo.filterVal
          : '',
        'filter.orderNo': filters.orderNo
          ? filters.orderNo.filterVal
          : '',
        'filter.storehouseCoordinatesOrName': filters[
          'sourceStorehouse.displayName'
        ]
          ? filters['sourceStorehouse.displayName'].filterVal
          : '',
        'filter.recipientName': filters['recipient.displayName']
          ? filters['recipient.displayName'].filterVal
          : '',
        'filter.officeDisplayName': filters['office.displayName']
          ? filters['office.displayName'].filterVal
          : '',
        'filter.costCenterDisplayName': filters['costCenter.displayName']
          ? filters['costCenter.displayName'].filterVal
          : '',
        'filter.orderTypeDisplayName': filters['orderType.displayName']
          ? filters['orderType.displayName'].filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '' : '-'}${sortField}`)
    }

    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }

  const handleStructureClick = useCallback(
    (dataField) => (e) => {
      e.stopPropagation()
      switch (dataField) {
        case 'sourceStorehouse.displayName':
          setOpenStorehouseFilter((prev) => !prev)
          setOpenCostCenterFilter(false)
          setOpenOfficeFilter(false)
          setShowOverlayLeft(true)
          setOpenOrderProcessTypeFilter(false)
          break
        case 'office.displayName':
          setOpenStorehouseFilter(false)
          setOpenCostCenterFilter(false)
          setOpenOfficeFilter((prev) => !prev)
          setShowOverlayLeft(true)
          setOpenOrderProcessTypeFilter(false)
          break
        case 'costCenter.displayName':
          setOpenStorehouseFilter(false)
          setOpenOfficeFilter(false)
          setOpenCostCenterFilter((prev) => !prev)
          setShowOverlayLeft(true)
          setOpenOrderProcessTypeFilter(false)
          break
        case 'orderType.displayName':
          setOpenStorehouseFilter(false)
          setOpenOfficeFilter(false)
          setOpenCostCenterFilter(false)
          setShowOverlayLeft(true)
          setOpenOrderProcessTypeFilter((prev) => !prev)
          break
        default:
      }
    },
    [setOpenStorehouseFilter, setOpenOfficeFilter, setOpenCostCenterFilter, setOpenOrderProcessTypeFilter, setShowOverlayLeft]
  )
  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allCheckBoxesSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          case 'sourceStorehouse.displayName':
          case 'costCenter.displayName':
          case 'office.displayName':
          case 'orderType.displayName':
            return (
              <>
                <OverflowTooltip>
                  {column.text}
                  {components.sortElement}
                  <StyledHeaderStructure>
                    {components.filterElement}
                    <div className='ps-2'>
                      <StyledStructureButton
                        onClick={handleStructureClick(tmpColumn.dataField)}
                      >
                        <StructureIcon />
                      </StyledStructureButton>
                    </div>
                  </StyledHeaderStructure>
                </OverflowTooltip>
              </>
            )
          default:
            return (
              <>
                <OverflowTooltip>
                  {column.text}
                  {components.sortElement}
                  <div className='d-flex align-items-end'>
                    {components.filterElement}
                  </div>
                </OverflowTooltip>
              </>
            )
        }
      }
    })
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })
    const ignoreColumns = () => {
      var result = []

      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')
      return result
    }
    return tmpColumns.filter(
      (item) => !ignoreColumns().includes(item.dataField)
    )
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    handleStructureClick,
    noActions,
    withCheckbox,
  ])

  const getChosenFilters = () => {
    var message = []
    //save filters in Array
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('withdrawalsTable:alertMsg')
    //returns Alert if no results for used filters
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      return (
        <div className='no-data-placeholder-alert'>
          <Alert variant='info'>{alertMessage}</Alert>
        </div>
      )
    }
    //returns empty Table
    else {
      return (
        <div className='no-data-placeholder'>
          {t('withdrawalsTable:pleaseSearchWithdrawals')}
        </div>
      )
    }
  }
  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={withdrawals}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const WithdrawalTable = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useWithdrawalsState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <WithdrawalTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default WithdrawalTable
